import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { Icon, Loader } from '@lomarengas/design-system';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  addStatusToast,
  DataTable,
  DeleteConfirmation,
  SectionHeader,
} from '../../common/components';
import {
  deleteBlockedPeriod,
  getBlockedPeriods,
  selectBlockedPeriodDataTableItems,
  selectStatus,
} from './blockedPeriodsSlice';
import { LOADING_STATUS } from '../../common/constants';
import {
  getAccommodationDetailsBasicAsync,
  selectAccommodationDetailsBasic,
} from '../accommodations/accommodationsSlice';

const AccommodationBlockedPeriods = () => {
  const { accommodationId } = useParams<{ accommodationId: string }>();
  const dispatch = useAppDispatch();
  const blockedPeriodDataTableItems = useAppSelector(
    selectBlockedPeriodDataTableItems,
  );
  const [deletableBlockedPeriodId, setDeletableBlockedPeriodId] =
    useState<string>('');
  const [deleteConfirmationMessage, setDeleteConfirmationMessage] =
    useState<string>('');
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
    useState<boolean>(false);
  const status = useAppSelector(selectStatus);
  const accommodationBasic = useAppSelector(selectAccommodationDetailsBasic);

  const pageTitleBase = 'Estetyt jaksot kohteelle';
  const accommodationSuffix = useMemo(() => {
    if (!accommodationBasic) return '';
    const number = accommodationBasic.accommodationNumber;
    const translations = accommodationBasic.translations?.filter(
      item =>
        item.keyId === 'AccommodationName' &&
        item.locale?.toLowerCase() === 'fi_fi',
    );

    const anyName = translations?.[0]?.text || '';
    if (number && anyName) return ` - ${number} ${anyName}`;
    if (number) return ` - ${number}`;
    if (anyName) return ` - ${anyName}`;
    return '';
  }, [accommodationBasic]);

  const fullTitle = `${pageTitleBase}${accommodationSuffix}`;

  useEffect(() => {
    dispatch(getBlockedPeriods(accommodationId));
    if (accommodationId) {
      dispatch(getAccommodationDetailsBasicAsync(accommodationId));
    }
  }, [dispatch, accommodationId]);

  const showDeleteModal = (blockedPeriodId: string) => {
    const blockedPeriodById = blockedPeriodDataTableItems.find(
      a => a.ItemId === blockedPeriodId,
    );
    setDeletableBlockedPeriodId(blockedPeriodId);
    setDeleteConfirmationMessage(
      `Haluatko varmasti poistaa estetyn ajanjakson ${blockedPeriodById.Items[0]} - ${blockedPeriodById.Items[1]}?`,
    );
    setIsDeleteConfirmationVisible(true);
  };

  const handleDeleteBlockedPeriod = async (blockedPeriodId: string) => {
    console.log('Delete blocked period with id:', blockedPeriodId);
    try {
      await dispatch(deleteBlockedPeriod(blockedPeriodId)).unwrap();
      dispatch(
        addStatusToast({
          Title: 'Estojakson poisto',
          Message: 'Estojakso on poistettu onnistuneesti.',
        }),
      );
    } catch (error) {
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: 'Estojakson poisto epäonnistui.',
        }),
      );
    }
    setIsDeleteConfirmationVisible(false);
  };

  const AddNewButton = (
    <Link
      to={`/rates/blockedperiods/details/new?accommodationId=${accommodationId}`}
      className="btn btn-blue ps-16"
    >
      <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
      Lisää uusi estojakso
    </Link>
  );

  return (
    <>
      <Helmet>
        <title>{`Hinnoittelu - ${fullTitle}`}</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Title={fullTitle}
          Breadcrumbs={['Hinnoittelu']}
          Children={AddNewButton}
        />
        <div className="mb-24">
          <Link to={`/accommodations/details/${accommodationId}`}>
            Siirry kohteen tietoihin
          </Link>
        </div>
        <Loader IsLoading={status === LOADING_STATUS.LOADING} />
        {status === LOADING_STATUS.IDLE && (
          <DataTable
            ColumnWidths={[100, null]}
            Header={['Alkupäivä', 'Loppupäivä', 'Tyyppi', 'Kopioitava']}
            Items={blockedPeriodDataTableItems}
            LegendText={
              blockedPeriodDataTableItems.length > 1
                ? 'estettyä jaksoa'
                : 'estetty jakso'
            }
            TotalItems={blockedPeriodDataTableItems.length}
            DeleteItem={showDeleteModal}
          />
        )}
      </Container>

      <DeleteConfirmation
        ConfirmDelete={() =>
          handleDeleteBlockedPeriod(deletableBlockedPeriodId)
        }
        HideModal={() => setIsDeleteConfirmationVisible(false)}
        Id={deletableBlockedPeriodId}
        Message={deleteConfirmationMessage}
        ShowModal={isDeleteConfirmationVisible}
      />
    </>
  );
};

export default AccommodationBlockedPeriods;
